import React, { useRef, useEffect } from 'react';
import { TextField, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const HighlightedSpan = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

interface ScrollingTextFieldProps {
  chunks: string[];
  activeChunkText: string;
  placeholder?: string;
}

const ScrollingTextField = React.forwardRef<
  HTMLDivElement,
  ScrollingTextFieldProps
>(({ chunks, activeChunkText, placeholder, ...props }, ref) => {
  const textFieldRef = useRef<HTMLDivElement>(null);
  const chunkRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useEffect(() => {
    const activeIndex = chunks.findIndex(
      (chunk) => chunk.trim() === activeChunkText.trim()
    );

    if (activeIndex >= 0 && chunkRefs.current[activeIndex]) {
      const element = chunkRefs.current[activeIndex];
      const container = textFieldRef.current;

      if (element && container) {
        container.scrollTo({
          top: element.offsetTop - container.offsetTop - 30,
          behavior: 'smooth',
        });
      }
    }
    // console.log(activeChunkText)
  }, [activeChunkText, chunks]);

  const renderChunks = () => {
    return chunks.map((chunk, index) => {
      const normalizedChunk = chunk.trim().endsWith('.')
        ? chunk.trim()
        : chunk.trim() + '.';
      const normalizedActiveChunkText = activeChunkText.trim().endsWith('.')
        ? activeChunkText.trim()
        : activeChunkText.trim() + '.';
      const isActive = normalizedChunk === normalizedActiveChunkText;

      return (
        <span
          key={index}
          ref={(el) => {
            chunkRefs.current[index] = el;
          }}
          style={{ display: 'block', marginBottom: '8px' }}
        >
          {isActive ? <HighlightedSpan>{chunk}</HighlightedSpan> : chunk}
        </span>
      );
    });
  };
  return (
    <Box
      ref={textFieldRef}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.text.primary}`,
        borderRadius: '4px',
        padding: '8px',
        overflowY: 'auto',
        maxHeight: '140px',
        minHeight: '140px',
        whiteSpace: 'pre-wrap',
        width: '100%',
        color: theme.palette.text.primary,
      })}
    >
      {chunks.length > 0 ? renderChunks() : placeholder}
    </Box>
  );
});

export default ScrollingTextField;
